import styled, { css } from 'styled-components';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import TabsStyle from 'components/Tabs/styles';
import BreadcrumbsStyle from 'components/Breadcrumbs/styles';
import { zIndex } from 'components/styles';
import { DEEP_BORDER_BOX } from 'common';

const {
  SPACING,
  FONT_SIZE,
  FONT_WEIGHT,
  FONT_FAMILY,
  LINE_HEIGHT,
  TYPOGRAPHY,
  COLORS,
} = Tokens;

/* Defaults */
export const TOOLBAR_HEIGHT = SPACING.SPACING_64;
const TAB_IN_TOOLBAR_HEIGHT = SPACING.SPACING_64;

const styles = {
  AppToolbar: styled.div`
    ${DEEP_BORDER_BOX}
    direction: ${({ $rtl }) => ($rtl ? 'rtl' : 'ltr')};
    display: flex;
    flex-wrap: nowrap;
    ${({ $deviceType }) => $deviceType === 'Mobile' && css`
      flex-wrap: wrap;
    `};
    position: relative;
    z-index: ${zIndex.APP_TOOLBAR};
    align-items: center;
    justify-content: space-between;
    min-height: ${TOOLBAR_HEIGHT};
    width: 100%;
    max-width: 100%;
    min-width: 100%;
    ${TabsStyle.TabScrollButtonContainer} {
      height: ${TAB_IN_TOOLBAR_HEIGHT};
      background-color: ${COLORS.TRANSPARENT};
      background-image: linear-gradient(to left,${COLORS.TRANSPARENT} 0%, ${COLORS.TRANSPARENT} 30%);
      ${({ right }) => right && css`
        background-image: linear-gradient(to right,${COLORS.TRANSPARENT} 0%, ${({ $theme }) => $theme && $theme.background} 30%);
        left: inherit;
        right: 0;
      `}
    }
    ${TabsStyle.Tabs} {
      background-color: ${({ $theme }) => $theme.background};
      border-bottom: 0;
    }
    ${TabsStyle.TabWrapper} {
      height: ${TAB_IN_TOOLBAR_HEIGHT};
      ${({ $tabBreakPoint }) => $tabBreakPoint && css`
        min-width: 5.5rem; // make Tab fit on a smaller display
      `};
      &:not(:hover) {
        background-color: ${({ $theme }) => $theme.background};
      }
    }
  `,
  Titles: styled.div`
    min-height: ${TOOLBAR_HEIGHT};
    height: ${TOOLBAR_HEIGHT};
    max-height: ${TOOLBAR_HEIGHT};
    overflow: hidden;
    min-width: calc(15% + ${SPACING.SPACING_64});
    max-width: calc(40% + ${SPACING.SPACING_64});
    ${({ $deviceType }) => $deviceType === 'Mobile' && css`
      min-width: calc(15% + ${SPACING.SPACING_16});
      max-width: calc(40% + ${SPACING.SPACING_16});
    `};
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: ${SPACING.SPACING_0} ${SPACING.SPACING_64} ${SPACING.SPACING_0} ${SPACING.SPACING_24};
    ${({ $rtl }) => $rtl && css`
      padding: ${SPACING.SPACING_0} ${SPACING.SPACING_24} ${SPACING.SPACING_0} ${SPACING.SPACING_64};
    `};
    ${({ $deviceType }) => $deviceType === 'Mobile' && css`
      padding: ${({ $rtl }) => ($rtl ? `0 ${SPACING.SPACING_16} 0 0` : `0 0 0 ${SPACING.SPACING_16}`)};
    `};
    ${BreadcrumbsStyle.Wrapper} {
      box-shadow: none;
    }
    ${BreadcrumbsStyle.NavItemsWrapper} {
      width: 100%;
    }
    ${BreadcrumbsStyle.CompactWrapper} {
      width: 100%;
    }
  `,
  Content: styled.div`
    margin: ${SPACING.SPACING_0} ${SPACING.SPACING_24} ${SPACING.SPACING_0} ${SPACING.SPACING_0};
    color: ${({ $theme }) => $theme.primaryContent};
  `,
  Title: styled.h2`
    font-weight:  ${FONT_WEIGHT.FONT_WEIGHT_REGULAR};
    line-height: ${LINE_HEIGHT.LINE_HEIGHT_NONE};
    font-family: ${FONT_FAMILY.NOKIA_PURE_TEXT};
    font-size: ${FONT_SIZE.FONT_SIZE_16};
    white-space: nowrap;
    overflow: visible;
    text-overflow: ellipsis;
    padding: ${SPACING.SPACING_0};
    margin: ${SPACING.SPACING_0};
    color: ${({ $theme }) => $theme.primaryContent};
    text-align: ${({ $rtl }) => ($rtl ? 'right' : 'left')};
  `,
  Subtitle: styled.h3`
    ${TYPOGRAPHY.CAPTION};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: ${SPACING.SPACING_0};
    margin: ${SPACING.SPACING_0};
    margin-top: 1px;
    color: ${({ $theme }) => $theme.secondaryContent};
    text-align: ${({ $rtl }) => ($rtl ? 'right' : 'left')};
  `,
  AppToolbarContent: styled.div`
    text-align: ${({ $rtl }) => ($rtl ? 'right' : 'left')};
    display: flex;
    flex-grow: 1;
    justify-content: flex-end;
    height: 100%;
    min-height: 3.5rem;
    align-items: center;
    margin-right: ${({ $rtl }) => ($rtl ? undefined : `${SPACING.SPACING_8}`)};
    margin-left: ${({ $rtl }) => ($rtl ? `${SPACING.SPACING_8}` : undefined)};
  `,
  AppToolbarButtonRow: styled.div`
    ${props => css`direction: ${(props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};`}
    flex: 0;
    justify-self: end;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    padding-right: ${({ $rtl }) => ($rtl ? `${SPACING.SPACING_16}` : undefined)};
    padding-left: ${({ $rtl }) => ($rtl ? undefined : `${SPACING.SPACING_16}`)};
    ${({ $deviceType }) => $deviceType === 'Mobile' && css`
      padding: ${({ $rtl }) => ($rtl ? `0 0 0 ${SPACING.SPACING_8}` : `0 ${SPACING.SPACING_8} 0 0`)};
    `};
    &:last-child {
      padding-left: 0;
      padding-right: 0;
    }
    & > span.trigger {
      margin: 0 ${SPACING.SPACING_8};
      &:last-child {
        ${({ $deviceType }) => $deviceType === 'Mobile' && css`
          margin: 0;
        `}
      }
    }
    & > span > span.trigger {
      margin: 0 ${SPACING.SPACING_8};
      &:last-child {
        ${({ $deviceType }) => $deviceType === 'Mobile' && css`
          margin: 0;
        `}
      }
    }
  `,
  AppToolbarSelectItem: styled.div`
    ${({ $deviceType }) => $deviceType !== 'Mobile' && css`
      margin-left: ${({ $rtl }) => ($rtl ? `${SPACING.SPACING_8}` : undefined)};
      margin-right: ${({ $rtl }) => ($rtl ? undefined : `${SPACING.SPACING_8}`)};
    `};
    ${({ $deviceType }) => $deviceType === 'Mobile' && css`
      margin: 0 ${SPACING.SPACING_16};
    `};
  `,
};

export default styles;
